import { useState } from "react";
import { CheckCircle2, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PlanCards } from "@/components/PlanCards";
import { TeamCard } from "@/components/TeamCard";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { ChangeEmailCard } from "@/components/ChangeEmailCard";
import { CoachPrefsCard } from "@/components/CoachPrefsCard";
import { BillingHistoryCard } from "@/components/BillingHistoryCard";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "");

function StatusBanner({ access, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (path, msg) => {
    setBusy(true);
    try { await api.post(path); await onChange(); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const { reason } = access;
  if (reason === "subscription")
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-status-active">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5" />
          <div>
            <div className="font-semibold text-emerald-900">{access.plan === "group" ? "Group plan · $100.00/month" : "Single plan · $24.00/month"}</div>
            <div className="text-sm text-emerald-800/80">
              {access.cancel_at_period_end ? `Cancelled — access ends ${fmtDate(access.current_period_end)}` : access.current_period_end ? `Renews ${fmtDate(access.current_period_end)}` : "Active"}
            </div>
          </div>
        </div>
        {access.cancel_at_period_end ? (
          <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => toggle("/payments/resume", "Subscription resumed")} data-testid="resume-subscription-button">Resume plan</Button>
        ) : (
          <Button variant="ghost" className="rounded-full text-slate-600" disabled={busy} data-testid="cancel-subscription-button"
            onClick={() => window.confirm("Cancel at the end of this billing period?") && toggle("/payments/cancel", "Subscription will cancel at period end")}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Cancel plan
          </Button>
        )}
      </div>
    );
  if (reason === "group") return null;
  if (reason === "admin")
    return <div className="rounded-2xl border bg-secondary/50 p-5 text-sm text-slate-600" data-testid="billing-status-admin">Admin account — full access without a plan.</div>;
  if (reason === "trial")
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-center gap-3" data-testid="billing-status-trial">
        <Clock className="h-5 w-5 text-amber-600" />
        <div className="text-sm text-amber-900"><b>{access.trial_days_left} day{access.trial_days_left === 1 ? "" : "s"} left</b> in your free trial (ends {fmtDate(access.trial_ends_at)}). Pick a plan to keep going.</div>
      </div>
    );
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-5 flex items-center gap-3" data-testid="billing-status-expired">
      <AlertTriangle className="h-5 w-5 text-red-600" />
      <div className="text-sm text-red-900"><b>Your free trial has ended.</b> Choose a plan to get back to your notes — nothing has been deleted.</div>
    </div>
  );
}

export default function BillingPage() {
  const { user, refresh } = useAuth();
  const access = user.access;
  const showPlans = !["subscription", "group"].includes(access.reason);
  return (
    <div className="space-y-8 fade-up max-w-4xl">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Plan, team & account</h1>
        <p className="text-slate-500 mt-1">Simple monthly pricing. Cancel anytime.</p>
      </div>
      <StatusBanner access={access} onChange={refresh} />
      {showPlans && <PlanCards />}
      {access.group && <TeamCard />}
      {showPlans && <p className="text-xs text-slate-500">Payments are processed securely by Stripe. Test mode: use card 4242 4242 4242 4242, any future date, any CVC.</p>}
      {access.subscription_status && <BillingHistoryCard />}
      <ChangeEmailCard />
      <CoachPrefsCard />
      <ChangePasswordCard />
    </div>
  );
}
